import clsx from "clsx";
import { type FunctionComponent, memo } from "react";

import type { ButtonVariantsProps } from "~/components/buttons/Button/buttonVariants.ts";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { formInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import useOnClickOutside from "~/hooks/useOnClickOutside.ts";
import DeliveryPeriodPickerPopover
	from "~/modules/billing/components/DeliveryPeriodFilter/components/DeliveryPeriodPickerPopover.tsx";
import type { DateRange } from "~/types/dateAndTimeTypes.ts";

import {
	computeDateRange,
	type TempDateRange,
	useDeliveryPeriodPicker,
} from "./useDeliveryPeriodPicker.ts";

type Props = ButtonVariantsProps & FormInputBaseVariants & {
	selectedPeriod: DateRange;
	onChange: (period: DateRange) => void;
};

const RequiredDeliveryPeriodFilter: FunctionComponent<Props> = memo(({
	selectedPeriod,
	onChange,
	inputSize,
}) => {
	const startDate = selectedPeriod.startDate;
	const endDate = selectedPeriod.endDate;

	const {
		isOpen,
		setIsOpen,
		refs,
		floatingStyles,
		context,
		getReferenceProps,
		getFloatingProps,
		tempDateRange,
		setTempDateRange,
		quickSelectOptions,
		activeQuickSelect,
		buttonText,
		now,
	} = useDeliveryPeriodPicker({ startDate, endDate });

	const handleRangeChange = (value: TempDateRange) => {
		const [tempStart, tempEnd] = value;
		setTempDateRange(value);
		const computed = computeDateRange(tempStart, tempEnd);
		// Only call onChange if we have valid dates
		if (computed.startDate && computed.endDate) {
			onChange({ startDate: computed.startDate, endDate: computed.endDate });
		}
	};

	const applyFilter = () => {
		const [tempStart, tempEnd] = tempDateRange;
		const computed = computeDateRange(tempStart, tempEnd);
		// Only call onChange if we have valid dates
		if (computed.startDate && computed.endDate) {
			onChange({ startDate: computed.startDate, endDate: computed.endDate });
		}
		setIsOpen(false);
	};

	const handlePopoverClose = () => {
		applyFilter();
	};

	useOnClickOutside(refs.floating, handlePopoverClose);

	return (
		<>
			<div className="relative">
				<button
					className={clsx(formInputBaseVariants({ inputSize }))}
					ref={refs.setReference}
					{...getReferenceProps({ onClick: (e) => e.stopPropagation() })}
				>
					<span>{buttonText}</span>
				</button>
			</div>

			<DeliveryPeriodPickerPopover
				isOpen={isOpen}
				context={context}
				refs={refs}
				floatingStyles={floatingStyles}
				getFloatingProps={getFloatingProps}
				tempDateRange={tempDateRange}
				onRangeChange={handleRangeChange}
				quickSelectOptions={quickSelectOptions}
				activeQuickSelect={activeQuickSelect}
				now={now}
			/>
		</>
	);
});

RequiredDeliveryPeriodFilter.displayName = "RequiredDeliveryPeriodFilter";

export default RequiredDeliveryPeriodFilter;